'use client';

import Link from '@eliadmin/core/src/components/link';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { FiPhoneCall, FiUser } from 'react-icons/fi';
import { IoCloseOutline } from 'react-icons/io5';
import { LuMenu } from 'react-icons/lu';
import { HiAtSymbol } from 'react-icons/hi';
import { FaRegUserCircle } from 'react-icons/fa';
import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Modal } from '../ui/modal';
import { LiaHandshakeSolid } from 'react-icons/lia';
import { DeferredMeetingForm } from '../reservation/deferred-meeting-form';
import {
  ReservationPerson,
  ReservationService,
} from '@eliadmin/core/src/api/reservation/reservation-api';
import { isCurrentOffersMenuItem, isOrganizedOffersMenuItem } from './menu-item-identity';
import { getVideovizitkyCopy } from '../videovizitky/copy';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  people?: ReservationPerson[];
  services?: ReservationService[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const actualCategories = props.categories
    ? props.categories.filter(category => category.slug?.trim())
    : [];

  const generalLayout = useGlobalStore(state => state.generalLayout);
  const locale = useLocaleStore(state => state.currentLocale);
  const videoCopy = getVideovizitkyCopy(locale);
  const { t } = useTranslations();
  const me = useCustomerStore(s => s.me);
  const { isOpen, setIsOpen } = useDisclosure();
  const [isOpenLevel2, setIsOpenLevel2] = useState(false);
  const [isOpenLevel3, setIsOpenLevel3] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoriesEntity | null>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<{ name: string; index: number } | null>(
    null,
  );

  const handleCloseAll = () => {
    setIsOpenLevel3(false);
    setIsOpenLevel2(false);
    setIsOpen(false);
    setSelectedCategory(null);
    setSelectedMenuItem(null);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger aria-label="Menu"
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <div className="relative flex cursor-pointer items-center justify-end gap-1 text-sm font-semibold uppercase text-primary md:gap-3 md:text-base lg:text-lg">
            <IoCloseOutline size={22} />
            {t('text1855')}
          </div>
          <span className="relative flex cursor-pointer items-center justify-end gap-1 text-sm font-semibold uppercase text-primary md:gap-3 md:text-base lg:text-lg">
            <LuMenu size={20} />
            {t('text3241')}
          </span>
        </SheetTrigger>
        <SheetContent
          side={props.side}
          className={cn(
            'top-[57px] flex h-[calc(100vh-57px)] w-full flex-col overflow-y-auto border-l-0 border-t border-t-[#E7E7E9] p-0 pb-24 sm:top-[74px] sm:h-[calc(100vh-74px)] sm:max-w-[440px] md:top-[100px] md:h-[calc(100vh-100px)]',
            'ea-mobile-sidebar__content',
            'hide-scroll-bar',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'hidden',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <div className="w-full px-4 py-6">
            {!me?.customer ? (
              <SheetClose className="w-full" asChild>
                <Button asChild className="w-full">
                  <Link prefetch={false} href={ROUTES.SIGN_IN()}>
                    <FiUser size={20} /> {t('text190')}
                  </Link>
                </Button>
              </SheetClose>
            ) : (
              <SheetClose asChild>
                <Link
                  prefetch={false}
                  href={ROUTES.PROFILE()}
                  className="flex h-[52px] w-full items-center justify-center gap-2 rounded-[16px] bg-secondary"
                >
                  <FaRegUserCircle size={20} className="text-foreground" />
                  <p className="text-sm font-normal text-foreground sm:text-base">
                    {me.customer.name} {me.customer.surname.charAt(0)}.
                  </p>
                </Link>
              </SheetClose>
            )}
          </div>
          <div className="max-h-[1px] min-h-[1px] w-full bg-[#E7E7E9]" />
          <div className="flex w-full flex-col items-start justify-start gap-2 px-4 py-6">
            <SheetClose asChild>
              <Link
                prefetch={false}
                href={ROUTES.VIDEOCARDS()}
                onClick={handleCloseAll}
                className="flex h-12 w-full items-center justify-between rounded-[16px] border border-[#E7E7E9] px-4 text-sm text-foreground hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary lg:hidden"
              >
                {videoCopy.navigation}
              </Link>
            </SheetClose>
            {props.menu?.filter(item => !isOrganizedOffersMenuItem(item.path)).map((item, index) => {
              const toursRoute = ROUTES.TOURS(locale);
              const actualOffers = isCurrentOffersMenuItem(item.path, toursRoute);
              const organizedOffers = isOrganizedOffersMenuItem(item.path);
              const categoriesToMap = actualOffers ? actualCategories : props.categories || [];

              const Comp = item.path
                ? isRelativeHref(getSafeHref(item.path))
                  ? Link
                  : 'a'
                : 'span';
              return (
                <>
                  {actualOffers || organizedOffers ? (
                    <Sheet
                      key={index}
                      open={isOpenLevel2 && selectedMenuItem?.index === index}
                      onOpenChange={open => {
                        setIsOpenLevel2(open);
                        setSelectedMenuItem(open ? { name: item.name, index } : null);
                      }}
                    >
                      <SheetTrigger className="flex h-12 w-full items-center justify-between rounded-[16px] border border-[#E7E7E9] px-4 text-sm">
                        {item.name}
                        <MdOutlineKeyboardArrowRight size={20} className="text-primary" />
                      </SheetTrigger>
                      <SheetContent
                        className={cn(
                          'top-[155px] flex h-[calc(100vh-155px)] w-full flex-col items-start justify-start gap-2 overflow-y-auto border-none px-6 py-6 shadow-none sm:top-[198px] sm:h-[calc(100vh-198px)] sm:max-w-[440px]',
                          'ea-mobile-sidebar__layered-sheet-content',
                        )}
                      >
                        <div
                          className="absolute -top-[156px] right-0 z-[9999] h-16 w-[100vw] bg-transparent"
                          onClick={() => handleCloseAll()}
                        />
                        <SheetClose className="flex items-center justify-start gap-3">
                          <MdOutlineKeyboardArrowLeft size={20} className="text-primary" />
                          <p className="text-lg font-bold text-foreground">
                            {selectedMenuItem?.name}
                          </p>
                        </SheetClose>
                        <div className="flex w-full flex-col items-start justify-start gap-2">
                          {categoriesToMap.map(category => (
                            <Sheet
                              key={category.slug}
                              open={isOpenLevel3 && selectedCategory?.slug === category.slug}
                              onOpenChange={open => {
                                setIsOpenLevel3(open);
                                setSelectedCategory(open ? category : null);
                              }}
                            >
                              <div className="flex h-12 w-full items-center justify-between rounded-[16px] px-4 text-sm">
                                {category.childs && category.childs.length > 0 ? (
                                  <>
                                    <SheetClose asChild>
                                      <Link
                                        prefetch={false}
                                        href={`${category.slug}?type=${
                                          actualOffers ? 'aktualne' : 'uskutocnene'
                                        }`}
                                        className="flex-1 text-sm"
                                        onClick={() => handleCloseAll()}
                                      >
                                        {category.name}
                                      </Link>
                                    </SheetClose>
                                    <SheetTrigger asChild>
                                      <MdOutlineKeyboardArrowRight
                                        size={20}
                                        className="text-primary"
                                      />
                                    </SheetTrigger>
                                  </>
                                ) : (
                                  <SheetClose asChild>
                                    <Link
                                      prefetch={false}
                                      href={`${category.slug}?type=${
                                        actualOffers ? 'aktualne' : 'uskutocnene'
                                      }`}
                                      className="flex h-12 w-full items-center justify-between rounded-[16px] bg-[#F6F6F6] text-sm"
                                      onClick={() => handleCloseAll()}
                                    >
                                      {category.name}
                                    </Link>
                                  </SheetClose>
                                )}
                              </div>
                              {category.childs && category.childs.length > 0 && (
                                <SheetContent
                                  className={cn(
                                    'top-[155px] flex h-[calc(100vh-155px)] w-full flex-col items-start justify-start gap-2 overflow-y-auto border-none px-6 py-6 shadow-none sm:top-[198px] sm:h-[calc(100vh-198px)] sm:max-w-[440px]',
                                    'ea-mobile-sidebar__layered-sheet-content',
                                  )}
                                >
                                  <div
                                    className="absolute -top-[156px] right-0 z-[9999] h-16 w-[100vw] bg-transparent"
                                    onClick={() => handleCloseAll()}
                                  />
                                  <SheetClose className="flex items-center justify-start gap-3">
                                    <MdOutlineKeyboardArrowLeft
                                      size={20}
                                      className="text-primary"
                                    />
                                    <p className="text-lg font-bold text-foreground">
                                      {category.name}
                                    </p>
                                  </SheetClose>
                                  <div className="flex w-full flex-col items-start justify-start gap-2">
                                    {category.childs.map(child => (
                                      <SheetClose key={child.slug} asChild>
                                        <Link
                                          prefetch={false}
                                          href={`${child.slug}?type=${
                                            actualOffers ? 'aktualne' : 'uskutocnene'
                                          }`}
                                          className="flex h-12 w-full items-center justify-between rounded-[16px] bg-[#F6F6F6] px-4 text-sm"
                                          onClick={() => handleCloseAll()}
                                        >
                                          {child.name}
                                        </Link>
                                      </SheetClose>
                                    ))}
                                  </div>
                                </SheetContent>
                              )}
                            </Sheet>
                          ))}
                        </div>
                      </SheetContent>
                    </Sheet>
                  ) : (
                    <SheetClose key={index} asChild>
                      <Comp
                        href={
                          actualOffers
                            ? `${toursRoute}?type=aktualne`
                            : getSafeHref(item?.path ?? '')
                        }
                        className="flex h-12 w-full items-center justify-between rounded-[16px] border border-[#E7E7E9] px-4"
                        prefetch={false}
                      >
                        <div
                          className={cn('text-sm font-normal text-foreground', {
                            'text-accent': props.menu?.length === index + 1,
                          })}
                        >
                          {item.name}
                        </div>
                      </Comp>
                    </SheetClose>
                  )}
                </>
              );
            })}
            <Modal
              trigger={
                <Button className="w-full">
                  <LiaHandshakeSolid size={24} /> {t('text3967')}
                </Button>
              }
              title={t('text3968') ?? ''}
              className={{
                content: cn(
                  'ea-mobile-sidebar__modal-content',
                  'z-[5000] max-h-[88dvh] overflow-y-scroll rounded-none bg-[#F6F6F6] p-4 sm:max-h-[75dvh] sm:max-w-[1024px] sm:rounded-[16px] md:gap-8 md:rounded-[32px] md:p-8 lg:p-12 ',
                ),
              }}
            >
              <DeferredMeetingForm people={props.people!} services={props.services!} />
            </Modal>
          </div>
          <div className="max-h-[1px] min-h-[1px] w-full bg-[#E7E7E9]" />
          <div className="flex w-full flex-col items-start justify-start gap-4 px-4 py-6">
            <Link
              prefetch={false}
              href={`tel:${generalLayout.contact?.phone?.replace(/\s+/g, '')}`}
              target="_blank"
              className="flex w-fit items-center justify-start gap-2"
            >
              <div className="flex h-[52px] w-[52px] items-center justify-center rounded-[16px] bg-[#F6F6F6]">
                <FiPhoneCall size={24} className="text-primary" />
              </div>
              <div className="flex flex-col items-start justify-start gap-1">
                <span className="text-sm font-light text-primary lg:text-base">
                  {generalLayout.contact?.phone}
                </span>
                <p className="text-xs font-light text-[#969696] lg:text-sm">{t('text800')}</p>
              </div>
            </Link>
            <Link
              prefetch={false}
              href={`mailto:${generalLayout.contact?.email}`}
              target="_blank"
              className="flex w-fit items-center justify-start gap-2"
            >
              <div className="flex h-[52px] w-[52px] items-center justify-center rounded-[16px] bg-[#F6F6F6]">
                <HiAtSymbol size={24} className="text-primary" />
              </div>
              <div className="flex flex-col items-start justify-start gap-1">
                <span className="text-sm font-light text-primary lg:text-base">
                  {generalLayout.contact?.email}
                </span>
                <p className="text-xs font-light text-[#969696] lg:text-sm">{t('text2008')}</p>
              </div>
            </Link>
          </div>
          <SocialIcons
            className={{
              wrapper: 'flex items-center justify-center gap-4 md:items-center md:justify-center',
              button:
                'flex h-[52px] w-[52px] items-center justify-center rounded-[16px] !bg-[#F6F6F6] shadow-none transition-all hover:bg-[#F6F6F6]/90',
              icon: 'h-5 fill-primary stroke-primary text-primary',
            }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
